import Layout from '@comp/layout'
import SolutionMeta from '@comp/solution/solution-meta'
import SolutionHero from '@comp/solution/solution-hero'
import SolutionPillars, { Pillar } from '@comp/solution/solution-pillars'
import ProductHowItWorks, { Step } from '@comp/product/product-how-it-works'
import SolutionRelated, { RelatedItem } from '@comp/solution/solution-related'
import FinalCta from '@comp/home/final-cta'

const pillars: Pillar[] = [
  {
    title: 'Every directory, one identity graph',
    description:
      'Active Directory, OpenLDAP, Oracle Unified Directory, web APIs, HRIS systems: connect them all and treat them as one source of truth.',
    bullets: [
      'Active Directory (on-prem and Azure AD / Entra)',
      'OpenLDAP and generic LDAP v3',
      'Oracle Unified Directory (OUD)',
      'Any system reachable via REST or WebAPI',
    ],
  },
  {
    title: 'No migration, no rip-and-replace',
    description:
      'Keep your existing directory as the system of record. Monosign reads it, augments it, and presents one identity to every app.',
    bullets: [
      'Read-only or read-write mode',
      'Configurable attribute mapping',
      'Schema extension without DC changes',
      'Multi-domain and multi-forest support',
    ],
  },
  {
    title: 'Continuous, observable sync',
    description:
      'Profile changes flow in minutes, not nightly batches. Every sync run is observable, retryable, and reportable.',
    bullets: [
      'Delta sync for changed objects only',
      'Configurable polling and webhook triggers',
      'Per-source dashboards and alerts',
      'Replayable sync logs for audit',
    ],
  },
  {
    title: 'Identity as data, queryable',
    description:
      'Expose the unified directory through a REST API. Build reports, dashboards, and workflows on a single normalized identity object.',
    bullets: [
      'REST API for users, groups, and entitlements',
      'Versioned, paginated, predictable endpoints',
      'OData-style filters for downstream tools',
      'Webhook events on identity change',
    ],
  },
]

const steps: Step[] = [
  {
    title: 'Connect your sources',
    description:
      'Point Monosign at AD, LDAP, OUD, or any web API. Map attributes once; Monosign normalizes the rest.',
  },
  {
    title: 'Sync continuously',
    description:
      'Delta sync keeps profiles fresh. Webhook triggers handle joiner / mover / leaver events in near-real-time.',
  },
  {
    title: 'Query and act',
    description:
      'Use the unified REST API to drive entitlement reviews, dashboards, and downstream automations.',
  },
]

const related: RelatedItem[] = [
  {
    label: 'User lifecycle',
    description:
      'Joiner / mover / leaver workflows on top of the directory layer.',
    href: '/sign/lifecycle',
  },
  {
    label: 'Monosync Connectors',
    description:
      'Push identity changes downstream to every app that needs them.',
    href: '/sync/connectors',
  },
  {
    label: 'Joiner / mover / leaver',
    description:
      'Use case: lifecycle automation without spreadsheets.',
    href: '/solutions/joiner-mover-leaver',
  },
]

export default function DirectoryPage() {
  return (
    <Layout>
      <SolutionMeta
        title="Directory & sources"
        description="Connect AD, LDAP, OUD, and web APIs as identity sources. One normalized directory, queryable through REST, syncing continuously."
        path="/sign/directory"
      />
      <main className="bg-surface text-ink-primary">
        <SolutionHero
          eyebrow="Monosign · Directory & sources"
          title={
            <>
              Every directory.
              <br />
              One identity graph.
            </>
          }
          body="Connect Active Directory, LDAP, OUD, and any web API as identity sources. Monosign normalizes them into one identity, continuously synced and queryable through REST."
          badges={[
            'AD / LDAP / OUD / WebAPI',
            'Continuous delta sync',
            'No migration required',
          ]}
        />
        <SolutionPillars
          title={<>Your directories, unified, not replaced.</>}
          body="Monosign sits on top of the directories you already run, normalizes the shape, and gives every downstream system one consistent identity to talk to."
          pillars={pillars}
        />
        <ProductHowItWorks
          title={<>Three steps to a unified directory.</>}
          steps={steps}
        />
        <SolutionRelated items={related} />
        <FinalCta />
      </main>
    </Layout>
  )
}
